import { LocaleEnum, ScoreCriteriaDetailTypeEnum } from '../enums';

import { Dictionary, ExplainMessages } from '../models';

const EXPLAIN_MESSAGES_EN: ExplainMessages = {
  subtitle: `Automation does not have to sacrifice intervention.<br>Understand the whys and hows of each result with this tool.`,
  animation: {
    skip: 'Skip animation',
    replay: 'Replay animation'
  },
  search: {
    placeholder: 'Type a query'
  },
  loading: 'Loading...',
  allResultsLoaded: 'All results loaded',
  noDataFound: 'No results found',
  topResults: 'Top {count} results',
  clearSelection: 'Clear selection ({count})',
  score: {
    criterias: {
      [ScoreCriteriaDetailTypeEnum.CATALOG_DEFINITION]: {
        label: 'Catalog definition',
        abbr: 'CD',
        description: 'Shows how the product’s score varies depending on the values ​​of the Equalize and the query match',
        step: 'First, the search engine calculates how the score of a product varies depending on the values ​​of the Equalize and the query match.'
      },
      [ScoreCriteriaDetailTypeEnum.ORGANIC]: {
        label: 'Equalize',
        abbr: 'EQ',
        description: 'Changes the product’s score depending on the query match'
      },
      [ScoreCriteriaDetailTypeEnum.TERM_POSITION]: {
        label: 'Term position',
        abbr: 'TP',
        description: 'Changes the product’s score depending on the order of the words in the query'
      },
      [ScoreCriteriaDetailTypeEnum.BUSINESS_RULES]: {
        label: 'Business rules',
        abbr: 'BR',
        description: 'Shows how the product’s score changes when queries are configured to be boosted or buried',
        step: 'Then, it takes into account the different rules configured to see how they affect the products’ score.'
      },
      [ScoreCriteriaDetailTypeEnum.BASE_CONFIGURATION]: {
        label: 'Global base tooling',
        abbr: 'BT',
        description: 'Changes the product’s score depending on the boost or bury configured for all the queries'
      },
      [ScoreCriteriaDetailTypeEnum.QUERY_CONFIGURATION]: {
        label: 'Manual tooling',
        abbr: 'MT',
        description: 'Changes the product’s score depending on the boosts and buries configured for a specific query'
      },
      [ScoreCriteriaDetailTypeEnum.QUERY_CONTEXT]: {
        label: 'Wisdom of the crowd',
        abbr: 'WC',
        description: 'Changes the product’s score depending on the users’ behaviour: top products picked by all the users increase their score',
        step: 'Customers’ behaviour patterns are also considered: users’ interaction with the same product will increase the product’s score.'
      },
      [ScoreCriteriaDetailTypeEnum.USER_CONTEXT]: {
        label: 'User context',
        abbr: 'UC',
        description: 'Changes the product’s score depending on an individual user’s behaviour: top products picked by a user increase their score',
        step: 'As well as a user’s individual interaction: his preference for certain products will make their score go up!'
      },
      [ScoreCriteriaDetailTypeEnum.CUSTOM_SCORE]: {
        label: 'Custom score',
        abbr: 'CS',
        step: 'And finally, check how your custom source of scoring can be part of the products ranking.'
      },
      pzn: {
        label: 'Personalization',
        abbr: 'PZN',
        description: 'Changes the product’s score depending on the household behaviour: top products picked by them increase their score'
      }
    },
    max: 'Max',
    total: 'Total'
  },
  tiebreakers: {
    label: 'Tiebreakers',
    abbr: 'TB',
    description: 'Shows the criteria by which one product will be positioned higher than another when they have the same score'
  }
};

const EXPLAIN_MESSAGES_ES: ExplainMessages = {
  subtitle: `La automatización no tiene por qué sacrificar la intervención.<br>` +
    'Comprende los porqués y cómos de cada resultado con esta herramienta.',
  animation: {
    skip: 'Omitir animación',
    replay: 'Repetir animación'
  },
  search: {
    placeholder: 'Escribe una query'
  },
  loading: 'Cargando...',
  allResultsLoaded: 'Todos los resultados cargados',
  noDataFound: 'No se han encontrado resultados',
  topResults: 'Primeros {count} resultados',
  clearSelection: 'Limpiar selección ({count})',
  score: {
    criterias: {
      [ScoreCriteriaDetailTypeEnum.CATALOG_DEFINITION]: {
        label: 'Catalog definition',
        abbr: 'CD',
        description: 'Muestra como varía la puntuación del producto dependiendo del valor del Equalize y de la coincidencia de la búsqueda',
        step: 'Primero, el motor de búsqueda calcula cómo varía la puntuación de un producto dependiendo de los valores del Equalize y de la coincidencia de la búsqueda.'
      },
      [ScoreCriteriaDetailTypeEnum.ORGANIC]: {
        label: 'Equalize',
        abbr: 'EQ',
        description: 'Cambia la puntuación del producto en función de la coincidencia de la búsqueda'
      },
      [ScoreCriteriaDetailTypeEnum.TERM_POSITION]: {
        label: 'Term position',
        abbr: 'TP',
        description: 'Cambia la puntuación del producto en función del orden de las palabras en la búsqueda'
      },
      [ScoreCriteriaDetailTypeEnum.BUSINESS_RULES]: {
        label: 'Business rules',
        abbr: 'BR',
        description: 'Muestra como la puntuación del producto varía cuando las búsquedas se configuran con boost y bury',
        step: 'Después, tiene en cuenta las diferentes reglas configuradas para ver cómo afectan a la puntuación de los productos.'
      },
      [ScoreCriteriaDetailTypeEnum.BASE_CONFIGURATION]: {
        label: 'Global base tooling',
        abbr: 'BT',
        description: 'Cambia la puntuación del producto en función del boost o bury configurado para todas las búsquedas'
      },
      [ScoreCriteriaDetailTypeEnum.QUERY_CONFIGURATION]: {
        label: 'Manual tooling',
        abbr: 'MT',
        description: 'Cambia la puntuación del producto en función de los boosts o buries configurados para una búsqueda concreta'
      },
      [ScoreCriteriaDetailTypeEnum.QUERY_CONTEXT]: {
        label: 'Wisdom of the crowd',
        abbr: 'WC',
        description: 'Cambia la puntuación del producto en función del comportamiento de los usuarios: los productos más escogidos por los usuarios aumentan su puntuación',
        step: 'Los patrones de comportamiento de los usuarios también se tienen en cuenta: la interacción de los usuarios con el mismo producto hará que su puntuación se incremente.'
      },
      [ScoreCriteriaDetailTypeEnum.USER_CONTEXT]: {
        label: 'User context',
        abbr: 'UC',
        description: 'Cambia la puntuación del producto en función del comportamiento de un usuario individual: los productos más escogidos por ese usuario aumentan su puntuación',
        step: 'Así como la interacción individual de un usuario: su preferencia por determinados productos hará que sus puntuaciones aumenten.'
      },
      [ScoreCriteriaDetailTypeEnum.CUSTOM_SCORE]: {
        label: 'Custom score',
        abbr: 'CS',
        step: 'Y, finalmente, comprueba cómo tu criterio personalizado de puntuación puede ser parte del ranking de productos.'
      },
      pzn: {
        label: 'Personalization',
        abbr: 'PZN',
        description: 'Cambia la puntuación del producto en función del comportamiento de los usuarios de una unidad familiar: ' +
          'sus productos más escogidos aumentan su puntuación'
      }
    },
    max: 'Máx.',
    total: 'Total'
  },
  tiebreakers: {
    label: 'Tiebreakers',
    abbr: 'TB',
    description: 'Muestra los criterios por los cuales un producto se posicionará más alto que otro cuando tienen la misma puntuación'
  }
};

export const EXPLAIN_MESSAGES: Dictionary<{ explain: ExplainMessages }> = {
  [LocaleEnum.ENGLISH]: {
    explain: EXPLAIN_MESSAGES_EN
  },
  [LocaleEnum.SPANISH]: {
    explain: EXPLAIN_MESSAGES_ES
  }
};
